import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { 
  ArrowLeft, Save, Upload, Loader2, FileText, 
  Home, Package, Settings as SettingsIcon, LogOut,
  Clock, AlertTriangle, Pencil, X, Check
} from "lucide-react";
import { toast } from "sonner";

const Settings = ({ user, setUser, API }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  
  const [companyName, setCompanyName] = useState(user?.company_name || "Trade Office");
  const [hourlyRate, setHourlyRate] = useState(user?.hourly_rate || 50);
  const [marginPercentage, setMarginPercentage] = useState(user?.margin_percentage || 20);
  const [logoUrl, setLogoUrl] = useState(user?.logo_url || null);
  const [hasFirstHourPremium, setHasFirstHourPremium] = useState(user?.has_first_hour_premium || false);
  const [firstHourRate, setFirstHourRate] = useState(user?.first_hour_rate || 75);
  const [emergencyCalloutFee, setEmergencyCalloutFee] = useState(user?.emergency_callout_fee || 0);
  
  // Profile edit fields
  const [editName, setEditName] = useState(user?.name || "");
  const [editEmail, setEditEmail] = useState(user?.email || "");
  const [editCompanyName, setEditCompanyName] = useState(user?.company_name || "Trade Office");
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          company_name: companyName,
          hourly_rate: hourlyRate,
          margin_percentage: marginPercentage,
          has_first_hour_premium: hasFirstHourPremium,
          first_hour_rate: hasFirstHourPremium ? firstHourRate : null,
          emergency_callout_fee: emergencyCalloutFee
        })
      });
      
      if (!response.ok) throw new Error("Failed to save");
      
      const updated = await response.json();
      setUser(updated);
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };
  
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await fetch(`${API}/settings/logo`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
      
      if (!response.ok) throw new Error("Failed to upload");
      
      const data = await response.json();
      setLogoUrl(data.logo_url);
      toast.success("Logo uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
      navigate("/", { replace: true });
    } catch (error) {
      navigate("/", { replace: true });
    }
  };

  const handleEditProfile = () => {
    setEditName(user?.name || "");
    setEditEmail(user?.email || "");
    setEditCompanyName(user?.company_name || "Trade Office");
    setEditingProfile(true);
  };

  const handleCancelEdit = () => {
    setEditingProfile(false);
  };

  const handleSaveProfile = async () => {
    if (!editName.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!editEmail.trim() || !editEmail.includes("@")) {
      toast.error("Valid email is required");
      return;
    }

    setSavingProfile(true);
    try {
      const response = await fetch(`${API}/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: editName.trim(),
          email: editEmail.trim().toLowerCase(),
          company_name: editCompanyName.trim()
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to update profile");
      }

      const updated = await response.json();
      setUser(updated);
      setCompanyName(updated.company_name);
      setEditingProfile(false);
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error(error.message || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold">Settings</h1>
            </div>

            <nav className="flex items-center gap-2">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon">
                  <Home className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/materials">
                <Button variant="ghost" size="icon">
                  <Package className="w-5 h-5" />
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="space-y-6">
          {/* Company Branding */}
          <Card className="bg-card border-white/5">
            <CardHeader>
              <CardTitle className="text-lg">Company Branding</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo */}
              <div className="space-y-4">
                <Label>Company Logo</Label>
                <div className="flex items-center gap-6">
                  <div className="w-24 h-24 bg-secondary rounded-lg flex items-center justify-center overflow-hidden border border-border">
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt="Company logo" 
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <FileText className="w-10 h-10 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleLogoUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      variant="secondary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-logo-btn"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Logo
                        </>
                      )}
                    </Button>
                    <p className="text-sm text-muted-foreground mt-2">
                      PNG, JPG up to 2MB
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Company Name */}
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  data-testid="company-name-input"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="bg-input/50 border-white/10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card className="bg-card border-white/5">
            <CardHeader>
              <CardTitle className="text-lg">Pricing Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Hourly Rate */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Standard Hourly Rate</Label>
                  <span className="mono text-2xl font-bold text-primary">
                    £{hourlyRate.toFixed(2)}
                  </span>
                </div>
                <Slider
                  value={[hourlyRate]}
                  onValueChange={(v) => setHourlyRate(v[0])}
                  min={10}
                  max={200}
                  step={5}
                  className="w-full"
                  data-testid="hourly-rate-slider"
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>£10/hr</span>
                  <span>£200/hr</span>
                </div>
              </div>

              {/* First Hour Premium */}
              <div className="space-y-4 p-4 bg-secondary/20 rounded-lg border border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <Label>First Hour Premium</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">Charge more for first hour to cover travel</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setHasFirstHourPremium(!hasFirstHourPremium)}
                    className={`w-12 h-6 rounded-full transition-colors ${hasFirstHourPremium ? 'bg-primary' : 'bg-muted'} relative`}
                    data-testid="first-hour-toggle"
                  >
                    <span className={`block w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${hasFirstHourPremium ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                
                {hasFirstHourPremium && (
                  <div className="space-y-3 pt-2 border-t border-white/10">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">First Hour Rate</Label>
                      <span className="mono text-lg font-bold text-primary">£{firstHourRate}/hr</span>
                    </div>
                    <Slider
                      value={[firstHourRate]}
                      onValueChange={(v) => setFirstHourRate(v[0])}
                      min={hourlyRate}
                      max={300}
                      step={5}
                      className="w-full"
                      data-testid="first-hour-rate"
                    />
                    <p className="text-xs text-muted-foreground">
                      First hour: £{firstHourRate}, then £{hourlyRate}/hr after
                    </p>
                  </div>
                )}
              </div>

              {/* Emergency Callout Fee */}
              <div className="space-y-4 p-4 bg-secondary/20 rounded-lg border border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    <div>
                      <Label>Emergency Callout Fee</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">Applied when quote mentions "emergency"</p>
                    </div>
                  </div>
                  <span className="mono text-lg font-bold text-primary">£{emergencyCalloutFee}</span>
                </div>
                <Slider
                  value={[emergencyCalloutFee]}
                  onValueChange={(v) => setEmergencyCalloutFee(v[0])}
                  min={0}
                  max={200}
                  step={10}
                  className="w-full"
                  data-testid="emergency-fee"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>£0 (disabled)</span>
                  <span>£200</span>
                </div>
              </div>
              
              {/* Margin */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Profit Margin</Label>
                  <span className="mono text-2xl font-bold text-primary">
                    {marginPercentage}%
                  </span>
                </div>
                <Slider
                  value={[marginPercentage]}
                  onValueChange={(v) => setMarginPercentage(v[0])}
                  min={0}
                  max={100}
                  step={5}
                  className="w-full"
                  data-testid="margin-slider"
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>0%</span>
                  <span>100%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card className="bg-card border-white/5">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Account Information</CardTitle>
              {!editingProfile && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEditProfile}
                  data-testid="edit-profile-btn"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {editingProfile ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="editName">Name</Label>
                    <Input
                      id="editName"
                      data-testid="edit-name-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="bg-input/50 border-white/10"
                      placeholder="Your name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="editEmail">Email Address</Label>
                    <Input
                      id="editEmail"
                      type="email"
                      data-testid="edit-email-input"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      className="bg-input/50 border-white/10"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="editCompanyNameProfile">Company Name</Label>
                    <Input
                      id="editCompanyNameProfile"
                      data-testid="edit-company-name-input"
                      value={editCompanyName}
                      onChange={(e) => setEditCompanyName(e.target.value)}
                      className="bg-input/50 border-white/10"
                      placeholder="Your company name"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={savingProfile}
                      data-testid="cancel-edit-btn"
                      className="flex-1"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveProfile}
                      disabled={savingProfile}
                      data-testid="save-profile-btn"
                      className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      {savingProfile ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{user?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Company</p>
                    <p className="font-medium">{user?.company_name || "Not set"}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            className="w-full h-14 text-lg font-bold uppercase tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-6 h-6 mr-3" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Settings;
